import _ from 'lodash';

import { bindServices } from '@/services/api';
import { toPaginate, getFiltersGlobalFilters } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { CoreEnums } from '@/utils/domain/constants';

// DVA model for the `AISkillGroup` admin catalog (configuration page +
// inline CRUD from the Launchpad). Holds two flows side by side:
//
//   1. Manual CRUD — `{id, name, description, isActive}` + hidden
//      `sourceProvider: 0` (manual). Same as `aiAgentTeams`.
//   2. Git/Zip group import — `fetchImportGroupPreview` →
//      `fetchImportGroup`. The preview returns the full list of skills
//      the source would bring in (with per-skill status: new /
//      already-exists / conflict / invalid) so the user can review and
//      confirm before any DB write. Both effects share the same
//      payload shape modulo `name`/`description`/`allowSkillGroupOverride`
//      which the confirm call adds on top.
//
// `bindServices('aiSkillGroups')` only knows about the standard CRUD
// helpers (`getAll`/`getById`/`create`/`update`/`remove`/`activate`/
// `apiRoute`/`bulkAction`); the import endpoints below go through
// `api.apiRoute` so we don't have to extend the binder.
const api = bindServices('aiSkillGroups');
const { BulkActionType } = CoreEnums;

const namespace = 'aiSkillGroups';
// Backend base. Same path the EntitySelector in the skill form hits
// (`ai/skillGroup/paged`) — keeps reads/writes/admin-list consistent.
const fetchRoute = 'ai/skillGroup';
// `AISkillGroup` exposes `{id, name, description, isActive, created,
// updated, creator, modifier}` plus optional `code` and `sourceProvider`
// (for imports). For the manual admin catalog we lean on the generic
// `NameDescriptionIsActive` preset; `sourceProvider: 0` is always sent
// from the form so the manual flag is preserved on POST.
const fetchParams = 'NameDescriptionIsActive';

const initialPagination = { page: 1, count: 0, totalPages: 0 };

// Map the form's numeric `SkillSourceProvider` enum to the string
// variant the import endpoints require (per the doc's section 6.0
// note: "En CRUD de AISkillGroup usar sourceProvider numérico (0/1/2).
// En endpoints de import (import/preview, import) usar string enum
// ("git", "zip")"). Returns the body shape the backend expects for
// both `import/preview` and `import` (the latter just adds
// `name`/`description`/`allowSkillGroupOverride`/`groupId` on top).
const buildImportBody = (payload) => {
  const provider = Number(payload?.provider);
  if (provider === CoreEnums.SkillSourceProvider.Git) {
    return {
      sourceProvider: 'git',
      sourceUrl: payload?.sourceUrl?.trim?.() || payload?.sourceUrl,
    };
  }
  if (provider === CoreEnums.SkillSourceProvider.Zip) {
    return {
      sourceProvider: 'zip',
      ...(payload?.owner ? { owner: payload.owner } : {}),
      ...(payload?.sourcePath ? { sourcePath: payload.sourcePath } : {}),
      ...(payload?.zipContent ? { zipContent: payload.zipContent } : {}),
    };
  }
  return {};
};

const initialState = {
  list: [],
  pagination: initialPagination,
  orderBy: '',
  select: CoreQueries[fetchParams].paged.select,
  searchSelect: CoreQueries[fetchParams].paged.searchSelect,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  validationErrors: [],
  bulkActionErrorData: null,
  showBulkActionErrors: false,

  // ── Group import (Git/Zip) state ────────────────────────────────
  // The drawer (`ImportSkillGroupForm`) drives all three flags. We
  // keep them on this model — rather than the `aiSkills` model used
  // for single-skill imports — because the endpoints
  // (`ai/skillGroup/import/preview` and `ai/skillGroup/import`) live
  // under the group resource and refreshes after a successful import
  // must hit the group listing, not the skill one.
  showImportGroup: false,
  // Last preview response. When non-null the drawer renders the
  // review modal; setting it back to null closes the modal without
  // touching the drawer underneath.
  importPreview: null,
  // Holds the original request body so the confirm step can replay
  // the same parameters (provider, sourceUrl/zipContent, …) without
  // making the user re-fill the drawer.
  importPreviewRequest: null,
  // Last successful import response (createdSkills, conflicts, …).
  importResult: null,
  // If the user opens the drawer to add skills to an existing group,
  // this gets pre-filled so the confirm step sends `groupId` instead
  // of creating a new group.
  importGroupId: null,
};

const Model = {
  namespace,
  state: initialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const params = {
          ...CoreQueries[fetchParams].paged,
          pageSize,
          ...payload,
        };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        yield put({
          type: 'queryData',
          payload: { ...res, limit: payload.limit ?? pageSize, page: payload.page ?? 1 },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: payload.detail
            ? CoreQueries[fetchParams]?.itemDetail
            : { ...CoreQueries[fetchParams]?.item, ...(payload?.params && { ...payload.params }) },
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {
        /*  */
      }
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.create, { route: fetchRoute, data: payload.data });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.update, { route: fetchRoute, data: payload.data });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchDelete({ payload }, { call, put }) {
      try {
        const { id, view } = payload;
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: { state: false, item: null, detail: false, id: null },
          });
        }
        yield call(api.remove, { route: fetchRoute, id });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *fetchActivate({ payload }, { put, call }) {
      try {
        yield call(api.activate, {
          route: fetchRoute,
          id: payload.id,
          isActive: payload.state,
        });
        yield put({ type: 'itemActivate', payload });
      } catch (e) {
        /*  */
      }
    },

    *bulkDeleteItems({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const stateData = yield select((state) => state[namespace]);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateData.orderBy && { orderBy: stateData.orderBy }),
          ...(filters && { filter: filters }),
          select: stateData.select,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkActivateItems({ payload }, { call, put }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Activate,
          ids,
        });
        yield put({ type: 'bulkItemActivate', payload: { ids, isActive: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkDeactivateItems({ payload }, { call, put }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Deactivate,
          ids,
        });
        yield put({ type: 'bulkItemActivate', payload: { ids, isActive: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *setPaginationPage({ payload }, { put }) {
      yield put({ type: 'queryPaginationPage', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },

    // ── Group import (Git/Zip) effects ────────────────────────────
    //
    // Two-step flow that mirrors the single-skill import in
    // `aiSkills` but at the *group* level:
    //
    //   1. `fetchImportGroupPreview` hits `POST ai/skillGroup/import/preview`
    //      with `{sourceProvider, sourceUrl?, owner?, sourcePath?, zipContent?}`.
    //      We persist both the response (rendered by the preview modal)
    //      and the request body so the confirm step can replay it
    //      without making the user re-fill the drawer.
    //
    //   2. `fetchImportGroup` hits `POST ai/skillGroup/import` with the
    //      same body plus `{name, description, allowSkillGroupOverride}`.
    //      If `importGroupId` is set we reuse that group instead of
    //      creating a new one (per the doc's section 7.3).
    //
    // We use string enum values (`"git"` / `"zip"`) for these calls
    // because the import endpoints are explicit about that — the
    // numeric enum is only valid for the CRUD endpoints.
    *openImportGroup({ payload }, { put }) {
      yield put({
        type: 'queryOpenImportGroup',
        payload: {
          groupId: payload?.groupId ?? null,
        },
      });
    },

    *closeImportGroup(_, { put }) {
      yield put({ type: 'queryCloseImportGroup' });
    },

    *closeImportPreview(_, { put }) {
      yield put({ type: 'queryClosePreview' });
    },

    *fetchImportGroupPreview({ payload }, { call, put }) {
      try {
        const body = buildImportBody(payload);
        const res = yield call(api.apiRoute, {
          route: `${fetchRoute}/import/preview`,
          method: 'post',
          data: body,
        });
        yield put({
          type: 'queryImportPreview',
          payload: { preview: res, request: body },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *fetchImportGroup({ payload }, { call, put, select }) {
      try {
        const stateData = yield select((state) => state[namespace]);
        // The base body — provider + source — is preserved verbatim
        // from the preview step so the backend resolves to the exact
        // same source it scanned. Confirm-only fields go on top.
        const base = stateData.importPreviewRequest || buildImportBody(payload);
        const body = {
          ...base,
          ...(payload?.name ? { name: payload.name } : {}),
          ...(payload?.description ? { description: payload.description } : {}),
          allowSkillGroupOverride: !!payload?.allowSkillGroupOverride,
          ...(stateData.importGroupId ? { groupId: stateData.importGroupId } : {}),
        };

        const res = yield call(api.apiRoute, {
          route: `${fetchRoute}/import`,
          method: 'post',
          data: body,
        });

        yield put({
          type: 'queryImportGroup',
          payload: { result: res },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        // Refresh the catalog so the just-imported group surfaces.
        const pageSize = yield select((state) => state.global.pageSize);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateData.orderBy && { orderBy: stateData.orderBy }),
          ...(filters && { filter: filters }),
        };
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *toggleBulkActionErrorsDrawer({ payload }, { put }) {
      yield put({
        type: 'queryToggleBulkActionErrorsDrawer',
        payload,
      });
    },

    *clearBulkActionErrors(_, { put }) {
      yield put({ type: 'queryClearBulkActionErrors' });
    },
  },

  reducers: {
    queryData(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    itemActivate(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      return { ...state, list };
    },

    bulkItemActivate(state, { payload }) {
      const { ids, isActive } = payload;
      const idSet = new Set(ids);
      const list = state.list.map((item) => (idSet.has(item.id) ? { ...item, isActive } : item));
      return { ...state, list };
    },

    queryItem(state, { payload }) {
      return { ...state, item: payload };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
      };
    },

    editOrDetail(state, { payload }) {
      return { ...state, item: payload.item ?? {} };
    },

    queryOrderBy(state, { payload }) {
      return { ...state, orderBy: payload };
    },

    queryPaginationPage(state, { payload }) {
      return { ...state, pagination: { ...state.pagination, page: payload } };
    },

    success(state, { payload }) {
      return {
        ...state,
        ...payload,
        success: payload?.success ?? payload,
        validationErrors: [],
      };
    },

    errors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload.validationErrors || [],
        message: payload.message,
      };
    },

    queryOpenImportGroup(state, { payload }) {
      return {
        ...state,
        showImportGroup: true,
        importGroupId: payload?.groupId ?? null,
        importPreview: null,
        importPreviewRequest: null,
        importResult: null,
      };
    },

    queryCloseImportGroup(state) {
      return {
        ...state,
        showImportGroup: false,
        importGroupId: null,
        importPreview: null,
        importPreviewRequest: null,
        importResult: null,
      };
    },

    queryImportPreview(state, { payload }) {
      return {
        ...state,
        importPreview: payload.preview,
        importPreviewRequest: payload.request,
      };
    },

    queryClosePreview(state) {
      return {
        ...state,
        importPreview: null,
        importPreviewRequest: null,
      };
    },

    queryImportGroup(state, { payload }) {
      // Close everything on success so the list view (already
      // refreshed by the effect) is what the user sees next. The
      // result is parked on state so a future "import details" UI can
      // surface conflicts/skipped counts if desired.
      return {
        ...state,
        showImportGroup: false,
        importGroupId: null,
        importPreview: null,
        importPreviewRequest: null,
        importResult: payload.result,
      };
    },

    setBulkActionErrors(state, { payload }) {
      return {
        ...state,
        bulkActionErrorData: payload.errorData,
        showBulkActionErrors: true,
      };
    },

    queryToggleBulkActionErrorsDrawer(state, { payload }) {
      return { ...state, showBulkActionErrors: payload.open };
    },

    queryClearBulkActionErrors(state) {
      return { ...state, bulkActionErrorData: null, showBulkActionErrors: false };
    },

    reset() {
      return { ...initialState };
    },
  },
};

export { initialPagination };
export default Model;
